import { NextSeo } from 'next-seo';
import { memo, useMemo } from 'react';

import { useShortIdContext } from '@air/provider-short-id';

import { getClipOgImageUrl } from '~/components/Layouts/PublicBoardLayout/getClipOgImageUrl';
import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { createShortUrlClipInBoardRoute } from '~/constants/routes';
import { type ClipSeoData, getSeoTitle, getShareSeoDescription } from '~/swr-hooks/shortUrl/utils';

export type PublicBoardClipSEOProps = {
  clip: ClipSeoData;
  boardId: string;
};

export const PublicBoardClipSEO = memo(({ clip, boardId }: PublicBoardClipSEOProps) => {
  const { shortId } = useShortIdContext();
  const { workspaceName } = usePublicWorkspace();
  const url = `https://app.air.inc${createShortUrlClipInBoardRoute(shortId, boardId, clip.id)}`;

  const ogImageUrl = useMemo(
    () => getClipOgImageUrl(clip.assets.image, clip.width, clip.height),
    [clip.assets.image, clip.width, clip.height],
  );

  const openGraph = useMemo(
    () => ({ url, images: ogImageUrl ? [{ url: ogImageUrl, alt: '' }] : undefined }),
    [url, ogImageUrl],
  );

  return (
    <NextSeo
      nofollow
      noindex
      title={getSeoTitle('Asset', clip.title, workspaceName)}
      description={getShareSeoDescription(clip.description)}
      canonical={url}
      openGraph={openGraph}
    />
  );
});

PublicBoardClipSEO.displayName = 'PublicBoardClipSEO';
